import { signOut } from '@sazeo/auth';
import { check2faRequirement } from '@sazeo/auth/requireMfa';
import { isRtl } from '@sazeo/i18n';
import {
  RoleUIProvider,
  useMfaFactors,
  useResolvedNavTree,
  useTenantSecurityPolicy,
  useUserPreference,
} from '@sazeo/sdk';
import { BannerSlot, cn } from '@sazeo/ui';
import {
  Banknote,
  BarChart2,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  CalendarRange,
  CheckSquare2,
  ClipboardList,
  Clock,
  Code,
  DollarSign,
  FileText,
  FolderKanban,
  FolderOpen,
  Gift,
  HardDrive,
  Headphones,
  Home,
  Inbox,
  Key,
  LifeBuoy,
  LogOut,
  type LucideIcon,
  Megaphone,
  Menu,
  MessageSquare,
  Network,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Search,
  Settings2,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Target,
  Timer,
  Umbrella,
  UserPlus,
  Users,
  Users2,
  Wallet,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Outlet, useLocation, useRouteLoaderData } from 'react-router-dom';

import type { RootLoaderData } from '../App';
import { useMountedAddons } from '../addons/useMountedAddons';
import { NavContext } from '../contexts/NavContext';
import { useDemoMode } from '../lib/demoContext';
import { tenantFunctionJson } from '../lib/functionFetch';
import { captureTenantEvent, setTenantObservabilityUser } from '../lib/observability';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from '../lib/presence';
import { tenantSdkClient } from '../lib/sdk';
import { getTenantLoginRedirectUrl } from '../lib/tenantHost';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { BetaFeedbackButton } from './BetaFeedbackButton';
import { CmdKDialog, useCmdKShortcut } from './CmdKDialog';
import { DemoBanner } from './DemoBanner';
import { GlobalSearch, useGlobalSearchShortcut } from './GlobalSearch';
import { NavCustomizerDrawer } from './NavCustomizerDrawer';
import { NotificationBell } from './NotificationBell';
import { QuotaBanner } from './QuotaBanner';

// ---------------------------------------------------------------------------
// Badge pollers — Inbox unread, Calendar today, Tasks open.
// Every 30 s so counters stay roughly fresh without a realtime subscription.
// Network errors are swallowed to avoid flashing zeros while the Edge Function
// is still cold-starting.
// ---------------------------------------------------------------------------

function usePollingCount(url: string, extract: (json: unknown) => number): number {
  const [count, setCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const extractRef = useRef(extract);

  useEffect(() => {
    extractRef.current = extract;
  }, [extract]);

  useEffect(() => {
    async function fetch_() {
      try {
        const json = await tenantFunctionJson<unknown>(url);
        setCount(extractRef.current(json));
      } catch {
        // keep last known count
      }
    }

    void fetch_();
    intervalRef.current = setInterval(() => {
      void fetch_();
    }, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return count;
}

function useUnreadCount(): number {
  return usePollingCount('notifications/unread-count', (json) => {
    const parsed = json as { count?: number };
    return parsed.count ?? 0;
  });
}

function useTodayEventCount(): number {
  // Today boundary in user's local timezone so "today" matches the calendar
  // grid's definition. We still send ISO to the Edge Function — the server
  // bucket key is per-row occurrence_start expanded server-side.
  const range = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { from: start.toISOString(), to: end.toISOString() };
  }, []);
  return usePollingCount(
    `calendar/events?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    (json) => {
      if (Array.isArray(json)) return json.length;
      return 0;
    },
  );
}

function useOpenTaskCount(): number {
  return usePollingCount('tasks?assignee=me&status=open', (json) => {
    if (Array.isArray(json)) return json.length;
    return 0;
  });
}

type SidebarNavItem = {
  id: string;
  key: string;
  to: string;
  icon: LucideIcon;
  enabled: boolean;
  external?: boolean;
};

const NAV_ITEMS_FALLBACK: SidebarNavItem[] = [
  { key: 'home', to: '/home', icon: Home, enabled: true },
  { key: 'people', to: '/people', icon: Users, enabled: true },
  { key: 'org_chart', to: '/org-chart', icon: Network, enabled: true },
  { key: 'nav_announcements', to: '/announcements', icon: Megaphone, enabled: true },
  { key: 'inbox', to: '/inbox', icon: Inbox, enabled: true },
  { key: 'documents', to: '/documents', icon: FolderOpen, enabled: true },
  { key: 'calendar', to: '/calendar', icon: CalendarDays, enabled: true },
  { key: 'reminders', to: '/reminders', icon: Bell, enabled: true },
  { key: 'tasks', to: '/tasks', icon: CheckSquare2, enabled: true },
  { key: 'reports', to: '/reports', icon: BarChart2, enabled: true },
  { key: 'messages', to: '/messages', icon: MessageSquare, enabled: true },
  { key: 'quotes', to: '/quotes', icon: FileText, enabled: true },
  { key: 'tickets', to: '/tickets', icon: Headphones, enabled: true },
  { key: 'files', to: '/files', icon: HardDrive, enabled: true },
  { key: 'search', to: '/search', icon: Search, enabled: true },
].map((item) => ({ ...item, id: item.key }));

const NAV_ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard: Home,
  Users,
  Network,
  Megaphone,
  Inbox,
  FolderOpen,
  CalendarDays,
  CheckSquare: CheckSquare2,
  CheckSquare2,
  BarChart2,
  MessageSquare,
  FileText,
  Headphones,
  HardDrive,
  Search,
};

/**
 * Map manifest `icon` strings to lucide components. Unknown icons fall back
 * to `Boxes` so an unrecognised addon never breaks rendering.
 */
const ADDON_ICON_MAP: Record<string, LucideIcon> = {
  clock: Clock,
  box: Boxes,
  users: Users,
  home: Home,
  inbox: Inbox,
  calendar: CalendarDays,
  reminders: Bell,
  tasks: CheckSquare2,
  umbrella: Umbrella,
  package: Package,
  'book-open': BookOpen,
  briefcase: Briefcase,
};

function resolveAddonIcon(name: string): LucideIcon {
  return ADDON_ICON_MAP[name] ?? Boxes;
}

export function AppShell() {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;
  const { t, i18n } = useTranslation('core.nav');
  const { t: tAddons } = useTranslation();
  const isDemo = useDemoMode();

  useEffect(() => {
    document.documentElement.dir = isRtl(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  const [collapsed, setCollapsed] = useUserPreference(tenantSdkClient, 'sidebar', false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [navCustomizerOpen, setNavCustomizerOpen] = useState(false);
  const cmdK = useCmdKShortcut();
  const location = useLocation();
  const globalSearch = useGlobalSearchShortcut();
  const unreadCount = useUnreadCount();
  const todayEventCount = useTodayEventCount();
  const openTaskCount = useOpenTaskCount();
  const hasTrackedAppOpenRef = useRef(false);

  const tenantId = data?.tenant.id ?? '';
  const userRole = data?.membership?.role_name ?? null;

  // 2FA enforcement: if the tenant policy requires 2FA for this role and the
  // user has no verified factor, redirect to the security settings page.
  const factorsQuery = useMfaFactors(tenantSdkClient);
  const policyQuery = useTenantSecurityPolicy(tenantSdkClient, tenantId);
  const needs2fa =
    !factorsQuery.isLoading &&
    !policyQuery.isLoading &&
    userRole !== null &&
    check2faRequirement(userRole, factorsQuery.data ?? [], policyQuery.data ?? null);

  const {
    mounted,
    isEnabled,
    installations,
    isLoading: addonsLoading,
  } = useMountedAddons(tenantId);
  const activeAddonIds = useMemo(
    () => installations.filter((entry) => entry.enabled).map((entry) => entry.addon_id),
    [installations],
  );
  const resolvedNavSections = useResolvedNavTree(
    tenantSdkClient,
    tenantId,
    activeAddonIds,
    userRole,
  );
  const fallbackById = useMemo(
    () => new Map(NAV_ITEMS_FALLBACK.map((item) => [item.id, item])),
    [],
  );
  const navItems = useMemo(() => {
    const items: SidebarNavItem[] = [];
    for (const section of resolvedNavSections) {
      for (const item of section.items) {
        // fallbackById lookup is for icon/key resolution only — not a visibility gate.
        // resolveNavTree already enforces requires_addon and requires_permission filters.
        const fallback = fallbackById.get(item.id);
        const icon = NAV_ICON_MAP[item.icon] ?? fallback?.icon ?? Boxes;
        const legacyLabelKey = item.label_key.startsWith('nav.')
          ? item.label_key.replace('nav.', 'nav_')
          : item.label_key;
        items.push({
          id: item.id,
          key: fallback?.key ?? legacyLabelKey,
          to: item.route,
          icon,
          enabled: true,
          external: item.external,
        });
      }
    }

    if (items.length === 0) return NAV_ITEMS_FALLBACK;

    if (!items.some((item) => item.id === 'search')) {
      const searchFallback = fallbackById.get('search');
      if (searchFallback) items.push(searchFallback);
    }

    return items;
  }, [fallbackById, resolvedNavSections]);

  /**
   * Addon menu entries are sorted by `order` from `mountExtensionPoints`.
   * Filtered by requires_role so members don't see admin-only addons.
   * Hidden while addon installations are still loading to prevent stale
   * registry state (useEffect reconciliation is async) from showing entries
   * for addons that are disabled for this tenant.
   */
  const addonMenuEntries = useMemo(() => {
    if (addonsLoading) return [];
    return mounted.menuEntries.filter((entry) => {
      if (!entry.requires_role || entry.requires_role.length === 0) return true;
      if (!userRole) return false;
      // Treat owner as a superset of admin for menu visibility.
      if (userRole === 'owner' && entry.requires_role.includes('admin')) return true;
      return entry.requires_role.includes(userRole);
    });
  }, [addonsLoading, mounted.menuEntries, userRole]);

  const handleSignOut = useCallback(async () => {
    try {
      setTenantObservabilityUser(null);
      await signOut();
    } finally {
      window.location.assign(getTenantLoginRedirectUrl(window.location.href));
    }
  }, []);

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px]',
      'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text-primary)]',
      isActive && 'bg-[var(--color-bg)] text-[var(--color-text-primary)] font-medium',
    );

  useEffect(() => {
    if (!data?.user.id) {
      setTenantObservabilityUser(null);
      return;
    }

    setTenantObservabilityUser({
      userId: data.user.id,
      tenantId: data.tenant.id,
    });
  }, [data?.tenant.id, data?.user.id]);

  useEffect(() => {
    if (!tenantId || hasTrackedAppOpenRef.current) {
      return;
    }

    captureTenantEvent('app.opened', {
      tenant_id: tenantId,
    });
    hasTrackedAppOpenRef.current = true;
  }, [tenantId]);

  useEffect(() => {
    startPresenceHeartbeat();
    return () => stopPresenceHeartbeat();
  }, []);

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    captureTenantEvent('page.viewed', {
      path: location.pathname,
      tenant_id: tenantId,
    });
  }, [location.pathname, tenantId]);

  // Close mobile drawer on any navigation
  useEffect(() => {
    void location.pathname;
    setMobileNavOpen(false);
  }, [location]);

  // Close mobile drawer on Escape
  useEffect(() => {
    if (!mobileNavOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileNavOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (cmdK.isOpen) {
      captureTenantEvent('cmd_k.opened', {
        tenant_id: tenantId || undefined,
      });
    }
  }, [cmdK.isOpen, tenantId]);

  // Redirect to 2FA enrollment if required and not on the security page already.
  if (needs2fa && !location.pathname.startsWith('/settings/security')) {
    return <Navigate to="/settings/security?require2fa=true" replace />;
  }

  return (
    <NavContext.Provider value={resolvedNavSections}>
      <div className="flex h-full min-h-screen w-full bg-[var(--color-bg)] text-[var(--color-text-primary)]">
        <GlobalSearch
          isOpen={globalSearch.isOpen}
          onOpenChange={(next) => (next ? globalSearch.open() : globalSearch.close())}
        />
        <CmdKDialog
          isOpen={cmdK.isOpen}
          onOpenChange={(open) => (open ? cmdK.open() : cmdK.close())}
        />
        <BetaFeedbackButton />
        <NavCustomizerDrawer
          open={navCustomizerOpen}
          onClose={() => setNavCustomizerOpen(false)}
          scope="user"
          supabase={tenantSdkClient}
          tenantId={tenantId}
        />
        {/* Mobile overlay */}
        {mobileNavOpen && (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        {/* Mobile drawer — slides in from left at < sm */}
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex sm:hidden transition-transform duration-150',
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <aside
            className="flex w-64 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]"
            aria-label="Primary navigation"
            data-testid="app-sidebar-mobile"
          >
            <div className="flex h-12 items-center justify-between px-3 border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2 overflow-hidden">
                {data?.tenant.logo_url ? (
                  <img
                    src={data.tenant.logo_url}
                    alt={data.tenant.name}
                    className="h-6 w-6 rounded-[4px] object-cover"
                  />
                ) : (
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-[4px] bg-[var(--color-accent)] text-[11px] font-semibold text-[var(--color-bg)]"
                    aria-hidden
                  >
                    {data?.tenant.name.charAt(0).toUpperCase() ?? 'S'}
                  </div>
                )}
                <span className="truncate text-[13px] font-medium text-[var(--color-text-primary)]">
                  {data?.tenant.name ?? 'Sazeo'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]"
              >
                <X size={14} />
              </button>
            </div>

            {/* Nav items — same as desktop, collapsed=false */}
            <nav className="flex-1 overflow-y-auto py-2">
              <ul className="flex flex-col gap-0.5 px-1.5">
                {navItems.map(({ key, to, icon: Icon, enabled, external }) => {
                  const NavIcon = Icon as LucideIcon;
                  const label = t(key);
                  let badgeValue = 0;
                  if (key === 'inbox') badgeValue = unreadCount;
                  else if (key === 'calendar') badgeValue = todayEventCount;
                  else if (key === 'tasks') badgeValue = openTaskCount;
                  const showBadge = badgeValue > 0;
                  const badgeLabel = badgeValue > 99 ? '99+' : String(badgeValue);
                  return (
                    <li key={key}>
                      {key === 'search' ? (
                        <button
                          type="button"
                          onClick={globalSearch.open}
                          className={navLinkClassName({ isActive: false })}
                        >
                          <Icon size={16} className="shrink-0" aria-hidden />
                          <span className="truncate">{label}</span>
                        </button>
                      ) : enabled ? (
                        external ? (
                          <a
                            href={to}
                            target="_blank"
                            rel="noreferrer"
                            className={navLinkClassName({ isActive: false })}
                          >
                            <span className="relative shrink-0">
                              <Icon size={16} aria-hidden />
                            </span>
                            <span className="truncate">{label}</span>
                          </a>
                        ) : (
                          <NavLink to={to} end className={navLinkClassName}>
                            <span className="relative shrink-0">
                              <Icon size={16} aria-hidden />
                              {showBadge && (
                                <span
                                  className={cn(
                                    'absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-semibold text-white',
                                    key === 'inbox'
                                      ? 'bg-[var(--color-error)]'
                                      : 'bg-[var(--color-accent)]',
                                  )}
                                  aria-label={`${badgeValue} ${label}`}
                                >
                                  {badgeLabel}
                                </span>
                              )}
                            </span>
                            <span className="truncate">{label}</span>
                          </NavLink>
                        )
                      ) : (
                        <span
                          aria-disabled="true"
                          title={label}
                          className="flex cursor-not-allowed items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px] text-[var(--color-text-tertiary)]"
                        >
                          <NavIcon size={16} className="shrink-0" aria-hidden />
                          <span className="truncate">{label}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {isEnabled('crm-contacts') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/crm-contacts" end className={navLinkClassName}>
                        <Users2 size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('core.contacts:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('expenses') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/expenses" end className={navLinkClassName}>
                        <Receipt size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.expenses:title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('recruitment') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/recruitment" end className={navLinkClassName}>
                        <UserPlus size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.recruitment:title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('payroll') && (userRole === 'admin' || userRole === 'owner') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/payroll" end className={navLinkClassName}>
                        <Banknote size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.payroll:title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('employee-schedules') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/schedules" end className={navLinkClassName}>
                        <CalendarRange size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('core.schedules:my_schedule')}</span>
                      </NavLink>
                    </li>
                    {(userRole === 'admin' || userRole === 'owner') && (
                      <li>
                        <NavLink
                          to="/schedules/manage"
                          end
                          className={navLinkClassName}
                          data-testid="schedule-manage-nav"
                        >
                          <CalendarRange size={16} className="shrink-0" aria-hidden />
                          <span className="truncate">
                            {tAddons('addon.employee_schedules:manage_page_title')}
                          </span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </>
              )}
              {isEnabled('performance-reviews') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/performance" end className={navLinkClassName}>
                        <Star size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('core.performance:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('contracts') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/contracts" end className={navLinkClassName}>
                        <FileText size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('core.contracts:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('asset-management') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/assets" end className={navLinkClassName}>
                        <Package size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.assets:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('training') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/training" end className={navLinkClassName}>
                        <BookOpen size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.training:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('helpdesk') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/helpdesk" end className={navLinkClassName}>
                        <LifeBuoy size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.helpdesk:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('projects') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/projects" end className={navLinkClassName}>
                        <FolderKanban size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.projects:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('invoicing') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/invoicing/suppliers" end className={navLinkClassName}>
                        <Building2 size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.suppliers:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('time-tracking') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/time-tracking" end className={navLinkClassName}>
                        <Timer size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.time-tracking:page_title')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('surveys') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/surveys" end className={navLinkClassName}>
                        <ClipboardList size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.surveys:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('budgets') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/budgets" end className={navLinkClassName}>
                        <Wallet size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.budgets:page_title')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('goals') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/goals" end className={navLinkClassName}>
                        <Target size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.goals:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('company-events') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/company-events" end className={navLinkClassName}>
                        <CalendarDays size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.company-events:menu_label')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('policies') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/policies" end className={navLinkClassName}>
                        <FileText size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.policies:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('disciplinary') && (userRole === 'admin' || userRole === 'owner') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/disciplinary" end className={navLinkClassName}>
                        <Shield size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.disciplinary:menu_label')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('salary-requests') && (userRole === 'admin' || userRole === 'owner') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/salary-requests" end className={navLinkClassName}>
                        <DollarSign size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.salary-requests:menu_label')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('benefits') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5">
                    <li>
                      <NavLink to="/addons/benefits" end className={navLinkClassName}>
                        <Gift size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.benefits:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('leave-management') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" data-testid="leave-management-nav">
                    <li>
                      <NavLink to="/addons/leave-management" end className={navLinkClassName}>
                        <CalendarDays size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.leave-management:menu_label')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('overtime-requests') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" data-testid="overtime-requests-nav">
                    <li>
                      <NavLink to="/addons/overtime-requests" end className={navLinkClassName}>
                        <Clock size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">
                          {tAddons('addons.overtime-requests:menu_label')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('procurement') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" data-testid="procurement-nav">
                    <li>
                      <NavLink to="/addons/procurement" end className={navLinkClassName}>
                        <ShoppingCart size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.procurement:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('wfh') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" data-testid="wfh-nav-mobile">
                    <li>
                      <NavLink to="/addons/wfh" end className={navLinkClassName}>
                        <Home size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.wfh:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {isEnabled('job-grades') && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" data-testid="job-grades-nav-mobile">
                    <li>
                      <NavLink to="/addons/job-grades" end className={navLinkClassName}>
                        <Briefcase size={16} className="shrink-0" aria-hidden />
                        <span className="truncate">{tAddons('addons.job-grades:menu_label')}</span>
                      </NavLink>
                    </li>
                  </ul>
                </>
              )}
              {addonMenuEntries.length > 0 && (
                <>
                  <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                  <ul className="flex flex-col gap-0.5 px-1.5" aria-label="Addons">
                    {addonMenuEntries.map((entry) => {
                      const AddonIcon = resolveAddonIcon(entry.icon);
                      const addonLabel = tAddons(entry.label_key, { defaultValue: entry.addonId });
                      return (
                        <li key={`${entry.addonId}-${entry.route}`}>
                          <NavLink
                            to={entry.route}
                            end
                            className={navLinkClassName}
                            title={addonLabel}
                          >
                            <AddonIcon size={16} className="shrink-0" aria-hidden />
                            <span className="truncate">{addonLabel}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </nav>

            <div className="border-t border-[var(--color-border)] px-1.5 py-2">
              <button
                type="button"
                onClick={() => setNavCustomizerOpen(true)}
                className={navLinkClassName({ isActive: false })}
              >
                <SlidersHorizontal size={16} className="shrink-0" aria-hidden />
                <span className="truncate">{t('nav_customizer_title')}</span>
              </button>
              <NavLink to="/settings/profile" className={navLinkClassName}>
                <Settings2 size={16} className="shrink-0" aria-hidden />
                <span className="truncate">{t('settings')}</span>
              </NavLink>
              {(userRole === 'admin' || userRole === 'owner') && (
                <NavLink
                  to="/api-keys"
                  className={navLinkClassName}
                  data-testid="api-keys-nav-mobile"
                >
                  <Key size={16} className="shrink-0" aria-hidden />
                  <span className="truncate">{t('nav_api_keys')}</span>
                </NavLink>
              )}
              {(userRole === 'admin' || userRole === 'owner') && (
                <NavLink
                  to="/settings/developer"
                  className={navLinkClassName}
                  data-testid="developer-settings-nav-mobile"
                >
                  <Code size={16} className="shrink-0" aria-hidden />
                  <span className="truncate">{t('developer')}</span>
                </NavLink>
              )}
              {(userRole === 'admin' || userRole === 'owner') && (
                <NavLink
                  to="/settings/integrations"
                  className={navLinkClassName}
                  data-testid="integrations-settings-nav-mobile"
                >
                  <Key size={16} className="shrink-0" aria-hidden />
                  <span className="truncate">{t('integrations')}</span>
                </NavLink>
              )}
              {(userRole === 'admin' || userRole === 'owner') && (
                <NavLink
                  to="/settings/audit"
                  className={navLinkClassName}
                  data-testid="audit-log-nav-mobile"
                >
                  <Shield size={16} className="shrink-0" aria-hidden />
                  <span className="truncate">{t('audit_log')}</span>
                </NavLink>
              )}
              <div className="mt-2 flex items-center gap-2 rounded-[4px] px-2 py-1.5">
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-bg)] text-[11px] font-semibold text-[var(--color-text-primary)] border border-[var(--color-border)]"
                  aria-hidden
                >
                  {data?.user.display_name.charAt(0).toUpperCase() ?? '?'}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[12px] font-medium">
                    {data?.user.display_name}
                  </span>
                  <span className="truncate text-[11px] text-[var(--color-text-tertiary)]">
                    {data?.user.email}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  aria-label={t('sign_out')}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]"
                >
                  <LogOut size={14} />
                </button>
              </div>
            </div>
          </aside>
        </div>

        {/* Desktop sidebar — hidden on mobile, always visible ≥ sm */}
        <aside
          className={cn(
            'hidden sm:flex shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]',
            'transition-[width] duration-150',
            collapsed ? 'w-14' : 'w-60',
          )}
          aria-label="Primary navigation"
          data-testid="app-sidebar"
        >
          <div className="flex h-12 items-center justify-between px-3 border-b border-[var(--color-border)]">
            <div
              className={cn(
                'flex items-center gap-2 overflow-hidden',
                collapsed && 'justify-center',
              )}
            >
              {data?.tenant.logo_url ? (
                <img
                  src={data.tenant.logo_url}
                  alt={data.tenant.name}
                  className="h-6 w-6 rounded-[4px] object-cover"
                />
              ) : (
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-[4px] bg-[var(--color-accent)] text-[11px] font-semibold text-[var(--color-bg)]"
                  aria-hidden
                >
                  {data?.tenant.name.charAt(0).toUpperCase() ?? 'S'}
                </div>
              )}
              {!collapsed && (
                <span className="truncate text-[13px] font-medium text-[var(--color-text-primary)]">
                  {data?.tenant.name ?? 'Sazeo'}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]"
            >
              {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-2">
            <ul className="flex flex-col gap-0.5 px-1.5">
              {navItems.map(({ key, to, icon: Icon, enabled, external }) => {
                const NavIcon = Icon as LucideIcon;
                const label = t(key);
                let badgeValue = 0;
                if (key === 'inbox') badgeValue = unreadCount;
                else if (key === 'calendar') badgeValue = todayEventCount;
                else if (key === 'tasks') badgeValue = openTaskCount;
                const showBadge = badgeValue > 0;
                const badgeLabel = badgeValue > 99 ? '99+' : String(badgeValue);
                return (
                  <li key={key}>
                    {key === 'search' ? (
                      <button
                        type="button"
                        onClick={globalSearch.open}
                        className={navLinkClassName({ isActive: false })}
                      >
                        <Icon size={16} className="shrink-0" aria-hidden />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </button>
                    ) : enabled ? (
                      external ? (
                        <a
                          href={to}
                          target="_blank"
                          rel="noreferrer"
                          className={navLinkClassName({ isActive: false })}
                          title={label}
                        >
                          <span className="relative shrink-0">
                            <Icon size={16} aria-hidden />
                          </span>
                          {!collapsed && <span className="truncate">{label}</span>}
                        </a>
                      ) : (
                        <NavLink to={to} end className={navLinkClassName}>
                          <span className="relative shrink-0">
                            <Icon size={16} aria-hidden />
                            {showBadge && (
                              <span
                                className={cn(
                                  'absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-semibold text-white',
                                  key === 'inbox'
                                    ? 'bg-[var(--color-error)]'
                                    : 'bg-[var(--color-accent)]',
                                )}
                                aria-label={`${badgeValue} ${label}`}
                              >
                                {badgeLabel}
                              </span>
                            )}
                          </span>
                          {!collapsed && <span className="truncate">{label}</span>}
                        </NavLink>
                      )
                    ) : (
                      <span
                        aria-disabled="true"
                        title={label}
                        className="flex cursor-not-allowed items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px] text-[var(--color-text-tertiary)]"
                      >
                        <NavIcon size={16} className="shrink-0" aria-hidden />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            {isEnabled('crm-contacts') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="crm-contacts-nav">
                  <li>
                    <NavLink
                      to="/addons/crm-contacts"
                      end
                      className={navLinkClassName}
                      title={tAddons('core.contacts:page_title')}
                    >
                      <Users2 size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('core.contacts:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('expenses') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="expenses-nav">
                  <li>
                    <NavLink
                      to="/expenses"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.expenses:title')}
                    >
                      <Receipt size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.expenses:title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('recruitment') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="recruitment-nav">
                  <li>
                    <NavLink
                      to="/recruitment"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.recruitment:title')}
                    >
                      <UserPlus size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.recruitment:title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('payroll') && (userRole === 'admin' || userRole === 'owner') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="payroll-nav">
                  <li>
                    <NavLink
                      to="/payroll"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.payroll:title')}
                    >
                      <Banknote size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.payroll:title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('employee-schedules') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="employee-schedules-nav">
                  <li>
                    <NavLink
                      to="/schedules"
                      end
                      className={navLinkClassName}
                      title={tAddons('core.schedules:my_schedule')}
                    >
                      <CalendarRange size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('core.schedules:my_schedule')}</span>
                      )}
                    </NavLink>
                  </li>
                  {(userRole === 'admin' || userRole === 'owner') && (
                    <li>
                      <NavLink
                        to="/schedules/manage"
                        end
                        className={navLinkClassName}
                        title={tAddons('addon.employee_schedules:manage_page_title')}
                        data-testid="schedule-manage-nav"
                      >
                        <CalendarRange size={16} className="shrink-0" aria-hidden />
                        {!collapsed && (
                          <span className="truncate">
                            {tAddons('addon.employee_schedules:manage_page_title')}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  )}
                </ul>
              </>
            )}

            {isEnabled('performance-reviews') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="performance-reviews-nav">
                  <li>
                    <NavLink
                      to="/performance"
                      end
                      className={navLinkClassName}
                      title={tAddons('core.performance:page_title')}
                    >
                      <Star size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('core.performance:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('contracts') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="contracts-nav">
                  <li>
                    <NavLink
                      to="/addons/contracts"
                      end
                      className={navLinkClassName}
                      title={tAddons('core.contracts:page_title')}
                    >
                      <FileText size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('core.contracts:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('asset-management') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="asset-management-nav">
                  <li>
                    <NavLink
                      to="/addons/assets"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.assets:page_title')}
                    >
                      <Package size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.assets:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('training') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="training-nav">
                  <li>
                    <NavLink
                      to="/addons/training"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.training:page_title')}
                    >
                      <BookOpen size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.training:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('helpdesk') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="helpdesk-nav">
                  <li>
                    <NavLink
                      to="/addons/helpdesk"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.helpdesk:page_title')}
                    >
                      <LifeBuoy size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.helpdesk:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('projects') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="projects-nav">
                  <li>
                    <NavLink
                      to="/addons/projects"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.projects:page_title')}
                    >
                      <FolderKanban size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.projects:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('invoicing') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="invoicing-suppliers-nav">
                  <li>
                    <NavLink
                      to="/addons/invoicing/suppliers"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.suppliers:page_title')}
                    >
                      <Building2 size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.suppliers:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('time-tracking') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="time-tracking-nav">
                  <li>
                    <NavLink
                      to="/addons/time-tracking"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.time-tracking:page_title')}
                    >
                      <Timer size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.time-tracking:page_title')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('surveys') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="surveys-nav">
                  <li>
                    <NavLink
                      to="/addons/surveys"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.surveys:page_title')}
                    >
                      <ClipboardList size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.surveys:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('budgets') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="budgets-nav">
                  <li>
                    <NavLink
                      to="/addons/budgets"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.budgets:page_title')}
                    >
                      <Wallet size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.budgets:page_title')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('goals') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="goals-nav">
                  <li>
                    <NavLink
                      to="/addons/goals"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.goals:menu_label')}
                    >
                      <Target size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.goals:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('company-events') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="company-events-nav">
                  <li>
                    <NavLink
                      to="/addons/company-events"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.company-events:menu_label')}
                    >
                      <CalendarDays size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.company-events:menu_label')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('policies') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="policies-nav">
                  <li>
                    <NavLink
                      to="/addons/policies"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.policies:menu_label')}
                    >
                      <FileText size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.policies:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('disciplinary') && (userRole === 'admin' || userRole === 'owner') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="disciplinary-nav">
                  <li>
                    <NavLink
                      to="/addons/disciplinary"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.disciplinary:menu_label')}
                    >
                      <Shield size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.disciplinary:menu_label')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('salary-requests') && (userRole === 'admin' || userRole === 'owner') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="salary-requests-nav">
                  <li>
                    <NavLink
                      to="/addons/salary-requests"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.salary-requests:menu_label')}
                    >
                      <DollarSign size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.salary-requests:menu_label')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('benefits') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="benefits-nav">
                  <li>
                    <NavLink
                      to="/addons/benefits"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.benefits:menu_label')}
                    >
                      <Gift size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.benefits:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('leave-management') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="leave-management-nav">
                  <li>
                    <NavLink
                      to="/addons/leave-management"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.leave-management:menu_label')}
                    >
                      <CalendarDays size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.leave-management:menu_label')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('overtime-requests') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="overtime-requests-nav">
                  <li>
                    <NavLink
                      to="/addons/overtime-requests"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.overtime-requests:menu_label')}
                    >
                      <Clock size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">
                          {tAddons('addons.overtime-requests:menu_label')}
                        </span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('procurement') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="procurement-nav">
                  <li>
                    <NavLink
                      to="/addons/procurement"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.procurement:menu_label')}
                    >
                      <ShoppingCart size={15} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.procurement:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('wfh') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="wfh-nav">
                  <li>
                    <NavLink
                      to="/addons/wfh"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.wfh:menu_label')}
                    >
                      <Home size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.wfh:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {isEnabled('job-grades') && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul className="flex flex-col gap-0.5 px-1.5" data-testid="job-grades-nav">
                  <li>
                    <NavLink
                      to="/addons/job-grades"
                      end
                      className={navLinkClassName}
                      title={tAddons('addons.job-grades:menu_label')}
                    >
                      <Briefcase size={16} className="shrink-0" aria-hidden />
                      {!collapsed && (
                        <span className="truncate">{tAddons('addons.job-grades:menu_label')}</span>
                      )}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}

            {addonMenuEntries.length > 0 && (
              <>
                <div className="my-2 border-t border-[var(--color-border)]" aria-hidden />
                <ul
                  className="flex flex-col gap-0.5 px-1.5"
                  aria-label="Addons"
                  data-testid="addon-nav"
                >
                  {addonMenuEntries.map((entry) => {
                    const AddonIcon = resolveAddonIcon(entry.icon);
                    const addonLabel = tAddons(entry.label_key, {
                      defaultValue: entry.addonId,
                    });
                    return (
                      <li key={`${entry.addonId}-${entry.route}`}>
                        <NavLink
                          to={entry.route}
                          end
                          className={navLinkClassName}
                          title={addonLabel}
                        >
                          <AddonIcon size={16} className="shrink-0" aria-hidden />
                          {!collapsed && <span className="truncate">{addonLabel}</span>}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>

          <div className="border-t border-[var(--color-border)] px-1.5 py-2">
            <button
              type="button"
              onClick={() => setNavCustomizerOpen(true)}
              className={navLinkClassName({ isActive: false })}
            >
              <SlidersHorizontal size={16} className="shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{t('nav_customizer_title')}</span>}
            </button>
            <NavLink to="/settings/profile" className={navLinkClassName}>
              <Settings2 size={16} className="shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{t('settings')}</span>}
            </NavLink>

            {(userRole === 'admin' || userRole === 'owner') && (
              <NavLink
                to="/api-keys"
                className={navLinkClassName}
                title={t('nav_api_keys')}
                data-testid="api-keys-nav"
              >
                <Key size={16} className="shrink-0" aria-hidden />
                {!collapsed && <span className="truncate">{t('nav_api_keys')}</span>}
              </NavLink>
            )}

            {(userRole === 'admin' || userRole === 'owner') && (
              <NavLink
                to="/settings/developer"
                className={navLinkClassName}
                title={t('developer')}
                data-testid="developer-settings-nav"
              >
                <Code size={16} className="shrink-0" aria-hidden />
                {!collapsed && <span className="truncate">{t('developer')}</span>}
              </NavLink>
            )}

            {(userRole === 'admin' || userRole === 'owner') && (
              <NavLink
                to="/settings/integrations"
                className={navLinkClassName}
                title={t('integrations')}
                data-testid="integrations-settings-nav"
              >
                <Key size={16} className="shrink-0" aria-hidden />
                {!collapsed && <span className="truncate">{t('integrations')}</span>}
              </NavLink>
            )}

            {(userRole === 'admin' || userRole === 'owner') && (
              <NavLink
                to="/settings/audit"
                className={navLinkClassName}
                title={t('audit_log')}
                data-testid="audit-log-nav"
              >
                <Shield size={16} className="shrink-0" aria-hidden />
                {!collapsed && <span className="truncate">{t('audit_log')}</span>}
              </NavLink>
            )}

            <div
              className={cn(
                'mt-2 flex items-center gap-2 rounded-[4px] px-2 py-1.5',
                collapsed && 'justify-center',
              )}
            >
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-bg)] text-[11px] font-semibold text-[var(--color-text-primary)] border border-[var(--color-border)]"
                aria-hidden
              >
                {data?.user.display_name.charAt(0).toUpperCase() ?? '?'}
              </div>
              {!collapsed && (
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[12px] font-medium">
                    {data?.user.display_name}
                  </span>
                  <span className="truncate text-[11px] text-[var(--color-text-tertiary)]">
                    {data?.user.email}
                  </span>
                </div>
              )}
              {!collapsed && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  aria-label={t('sign_out')}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]"
                >
                  <LogOut size={14} />
                </button>
              )}
            </div>
          </div>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col">
          <BannerSlot slot="tenant.workspace.global" layout="strip" />
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-bg)] px-3">
            {/* Hamburger — mobile only */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              data-testid="app-bar-hamburger"
              className="sm:hidden inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
            >
              <Menu size={18} aria-hidden />
            </button>
            {/* Spacer so search stays right-aligned on desktop */}
            <span className="hidden sm:block" aria-hidden />
            <div className="flex items-center gap-1">
              <NotificationBell />
              <button
                type="button"
                onClick={globalSearch.open}
                aria-label={`${t('search')} (⌘/)`}
                title={`${t('search')} (⌘/)`}
                data-testid="app-bar-search-button"
                className={cn(
                  'inline-flex h-8 items-center gap-2 rounded-[var(--radius)] border border-[var(--color-border)]',
                  'bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-text-secondary)]',
                  'hover:bg-[var(--color-bg)]',
                )}
              >
                <Search size={14} aria-hidden />
                <kbd className="font-mono text-[10.5px] text-[var(--color-text-tertiary)]">⌘/</kbd>
              </button>
            </div>
          </div>
          <AppBreadcrumbs />
          <QuotaBanner />
          {isDemo && <DemoBanner />}
          <div className="min-w-0 flex-1 overflow-auto bg-[var(--color-bg)]">
            <RoleUIProvider supabase={tenantSdkClient} tenantId={tenantId} roleCacheKey={userRole}>
              <Outlet />
            </RoleUIProvider>
          </div>
        </main>
      </div>
    </NavContext.Provider>
  );
}
